import React, { useCallback, useEffect, useState } from 'react';
import './TasksPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// The practice's client tasks (docs/slack-pane.md): the same client_tasks rows the Slack
// app's Tasks tab renders, read and written through the session routes in
// routes/client_tasks.py. Staff only; the page is not on the client view's list.
//
// A task is billing work about a practice. Never a patient's name in a title or a note:
// the row also lands on a Slack canvas, outside the BAA.

const FILTERS = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
];

const EMPTY_FORM = { title: '', owner: '', notes: '' };

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString();
}

async function readError(response, fallback) {
  const body = await response.json().catch(() => ({}));
  return body.error || fallback;
}

function TasksPage({ selectedPractice }) {
  const practiceId = selectedPractice ? selectedPractice.id : null;
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/tasks?status=${encodeURIComponent(filter)}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error(await readError(response, 'Could not load tasks'));
      const data = await response.json();
      setTasks(Array.isArray(data.tasks) ? data.tasks : []);
    } catch (err) {
      setError(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [practiceId, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (event) => {
    event.preventDefault();
    if (!practiceId || !form.title.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/practices/${practiceId}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: form.title.trim(),
          owner: form.owner.trim() || null,
          notes: form.notes.trim() || null,
        }),
      });
      if (!response.ok) throw new Error(await readError(response, 'Could not add the task'));
      const created = await response.json();
      setForm(EMPTY_FORM);
      setMessage({ type: 'success', text: 'Task added.' });
      // A new task is open; it belongs on the Open and All lists, not on Closed.
      if (filter !== 'closed') setTasks((rows) => [created, ...rows]);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Done on an open task closes it (the backend stamps closed_by with the signed-in
  // user); Reopen on a closed one clears that. A row that no longer matches the filter
  // leaves the list rather than sitting there with the wrong badge.
  const handleToggle = async (task) => {
    const nextStatus = task.status === 'open' ? 'closed' : 'open';
    setTogglingId(task.id);
    setMessage(null);
    try {
      const response = await fetch(`${API_URL}/api/practices/${practiceId}/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!response.ok) throw new Error(await readError(response, 'Could not update the task'));
      const updated = await response.json();
      setTasks((rows) =>
        filter === 'all'
          ? rows.map((row) => (row.id === updated.id ? updated : row))
          : rows.filter((row) => row.id !== updated.id)
      );
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setTogglingId(null);
    }
  };

  if (!selectedPractice) {
    return (
      <div className="tasks-page">
        <p className="tasks-muted">Select a practice to see its tasks.</p>
      </div>
    );
  }

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div>
          <h1 className="page-title">Tasks</h1>
          <p className="tasks-muted">
            {selectedPractice.name} — billing work about the practice. No patient names in a title or a note.
          </p>
        </div>
        <div className="tasks-filter" role="group" aria-label="Task status filter">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`tasks-filter-btn ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className={`tasks-message ${message.type}`}>
          <span>{message.text}</span>
          <button type="button" className="message-close" onClick={() => setMessage(null)} aria-label="Dismiss">
            &times;
          </button>
        </div>
      )}

      <form className="tasks-add" onSubmit={handleAdd}>
        <input
          className="tasks-input tasks-input-title"
          placeholder="Add a task (title)"
          value={form.title}
          maxLength={200}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          disabled={saving}
          aria-label="Title"
        />
        <input
          className="tasks-input tasks-input-owner"
          placeholder="Owner"
          value={form.owner}
          maxLength={120}
          onChange={(e) => setForm({ ...form, owner: e.target.value })}
          disabled={saving}
          aria-label="Owner"
        />
        <input
          className="tasks-input tasks-input-notes"
          placeholder="Notes"
          value={form.notes}
          maxLength={5000}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          disabled={saving}
          aria-label="Notes"
        />
        <button type="submit" className="tasks-add-btn" disabled={saving || !form.title.trim()}>
          {saving ? 'Adding…' : 'Add task'}
        </button>
      </form>

      {error && (
        <div className="tasks-message error">
          <span>{error}</span>
          <button type="button" className="tasks-retry" onClick={load}>Retry</button>
        </div>
      )}

      {loading ? (
        <p className="tasks-muted">Loading tasks…</p>
      ) : !error && tasks.length === 0 ? (
        <p className="tasks-empty">
          {filter === 'open' ? 'No open tasks.' : filter === 'closed' ? 'No closed tasks.' : 'No tasks yet.'}
        </p>
      ) : (
        <div className="tasks-table-wrap">
          <table className="tasks-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Owner</th>
                <th>Notes</th>
                <th>Created</th>
                <th>Status</th>
                <th>Closed by</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id} className={`tasks-row ${task.status === 'closed' ? 'closed' : ''}`}>
                  <td className="tasks-title">{task.title}</td>
                  <td>{task.owner_user || <span className="tasks-muted">—</span>}</td>
                  <td className="tasks-notes">{task.notes || <span className="tasks-muted">—</span>}</td>
                  <td className="tasks-when" title={task.created_by ? `by ${task.created_by}` : undefined}>
                    {formatWhen(task.created_at)}
                  </td>
                  <td>
                    <span className={`status-badge status-${task.status}`}>{task.status}</span>
                  </td>
                  <td>
                    {task.status === 'closed'
                      ? `${task.closed_by || 'Unknown'}${task.closed_at ? ` · ${formatWhen(task.closed_at)}` : ''}`
                      : <span className="tasks-muted">—</span>}
                  </td>
                  <td className="tasks-actions">
                    <button
                      type="button"
                      className={`tasks-toggle ${task.status === 'open' ? 'done' : 'reopen'}`}
                      onClick={() => handleToggle(task)}
                      disabled={togglingId === task.id}
                    >
                      {task.status === 'open' ? 'Done' : 'Reopen'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TasksPage;
